import React from 'react';
import { Trash2 } from 'lucide-react';
import ItemCard from './ItemCard';
import InputField from './InputField';
import useFlipAnimation from '../hooks/useFlipAnimation';
import { ui } from '../ui';

const TH = 'px-3.5 py-2.5 text-left text-meta font-medium uppercase tracking-wide text-ink-2';

export default function CDMTable({
  rows, userName, identityKey, isFacilitator, revealed = true,
  probabilityMode = false, readOnly = false,
  addItem, editItem, deleteItem, deleteRow, vote, setProbability,
}) {
  const setFlipRef = useFlipAnimation();

  const canModify = (item) =>
    isFacilitator || (item.addedByKey ? item.addedByKey === identityKey : item.addedBy === userName);

  const renderColumn = (row, column, placeholder, opts = {}) => (
    <>
      <div className="mb-3 max-h-96 overflow-y-auto">
        {row[column].map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            onVote={(v) => vote(row.id, column, item.id, v)}
            onDelete={() => deleteItem(row.id, column, item.id, item.private)}
            onEdit={(text) => editItem(row.id, column, item.id, text, item.private)}
            canModify={canModify(item)}
            emphasis={column === 'diagnoses'}
            isSmall={opts.isSmall}
            revealed={revealed}
            readOnly={readOnly}
            probability={
              column === 'diagnoses' && probabilityMode
                ? { enabled: true, onSet: (v) => setProbability(row.id, item.id, v) }
                : null
            }
          />
        ))}
      </div>
      {!readOnly && (!opts.singleEntry || row[column].length === 0) && (
        <InputField placeholder={placeholder} onSubmit={(text) => addItem(row.id, column, text)} />
      )}
    </>
  );

  return (
    <div className={`${ui.panel} overflow-hidden`}>
      <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="bg-page border-b border-line">
              <th className={`${TH} w-[8%]`}>Likelihood</th>
              <th className={`${TH} w-[25%]`}>Diagnosis</th>
              <th className={`${TH} w-[33%]`}>Supporting</th>
              <th className={`${TH} w-[34%]`}>Opposing</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} ref={setFlipRef(row.id)} className="group border-t border-line bg-surface">
                {/* Critical rows get a red left rule, nothing else. */}
                <td className={`p-3.5 align-top ${row.isCritical ? 'border-l-[3px] border-l-flag-red' : ''}`}>
                  {row.isCritical ? (
                    <span className={ui.chipRed}>{row.displayOrder}</span>
                  ) : (
                    <span className="text-title font-semibold text-accent">{row.displayOrder}</span>
                  )}
                  {isFacilitator && !readOnly && (
                    <button
                      onClick={() => deleteRow(row.id, false)}
                      className={`card-tools ${ui.iconBtn} w-7 h-7 mt-2 hover:text-flag-red block`}
                      aria-label={`Delete row ${row.displayOrder}`}
                      title="Delete this row (facilitator)"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
                <td className="border-l border-line p-3.5 align-top">
                  {renderColumn(row, 'diagnoses', 'Add diagnosis...', { singleEntry: true })}
                </td>
                <td className="border-l border-line p-3.5 align-top">
                  {renderColumn(row, 'supporting', 'Add supporting...', { isSmall: true })}
                </td>
                <td className="border-l border-line p-3.5 align-top">
                  {renderColumn(row, 'opposing', 'Add opposing...', { isSmall: true })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
